import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { styled } from "@mui/material";
import Grid from "@mui/material/Grid";
import { CanceledError } from "axios";
import Guid from "common/values/guid/guid";
import IndividualAPIService from "marketplace/entities/individual/api/individual-api-service";
import Individual from "marketplace/entities/individual/individual";
import { enqueueSnackbar } from "notistack";
import React, { useEffect } from "react";
import { useSession } from "users/session/session-context";
import CommentThread from "work/entities/comment-thread/comment-thread";
import Proposal, { ProposalField, ProposalFieldCategory, } from "work/entities/proposal/proposal";
import VendorTeamReadOnly from "work/values/vendor-team/view/vendor-team-readonly";
import { Masked } from "work/entities/work-agreement/work-agreement";
import { HeadControls } from "work/entities/views/work-dialog-shared-components";

const ListContainer = styled(Grid)(
  () => ({
    minHeight: "20rem",
  }));
const TeamReview = styled(VendorTeamReadOnly)(
  ({ theme }) => ({
    [theme.breakpoints.down("lg")]: {
      marginTop: theme.spacing(2),
    },
    marginLeft: theme.spacing(2),
  }));

type TeamTabArchivedProps = {
  entityId: Guid;
  activeTab: ProposalFieldCategory;
  proposal: Proposal;
  commentThreads: CommentThread[];
  onCommentsClicked: (
    field: ProposalField,
    name?: string
  ) => void;
};

export default function TeamTabArchived(props: Readonly<TeamTabArchivedProps>) {
  const {
    activeTab,
    proposal,
    commentThreads,
    onCommentsClicked,
  } = props;

  const [selectedLeader, setSelectedLeader] = React.useState<Individual | null>(null);
  const [selectedMembers, setSelectedMembers] = React.useState<Individual[]>([]);

  const session = useSession();

  useEffect(
    () => {
      const fetchTeamMembers = async () => {
        if (Masked.isMasked(proposal.vendorTeam)) return;
        if (activeTab !== ProposalFieldCategory.Team) return null;
        if (!proposal.vendorTeam?.leader) return;

        try {
          const individualService = new IndividualAPIService(session);
          const individualIdsToFetch = [
            proposal.vendorTeam.leader.userId,
            ...proposal.vendorTeam.members.map((member) => member.userId)
          ];

          let abortController = new AbortController();
          const response = await individualService.getUsersProfileInfo(
            individualIdsToFetch,
            abortController
          );

          const leader = response.find((ind: Individual) => Masked.isPresentNotMasked(proposal.vendorTeam) &&
            ind.userId.isEqualTo(proposal.vendorTeam.leader?.userId));
          setSelectedLeader(leader ?? null);

          const members = response.filter((ind: Individual) =>
            (Masked.isNotMasked(proposal.vendorTeam) && !ind.userId.isEqualTo(proposal.vendorTeam?.leader?.userId))) ||
            [];
          setSelectedMembers(members);

          return () => {
            abortController.abort();
            abortController = new AbortController();
          };

        } catch (error) {
          if (!(error instanceof CanceledError)) {
            enqueueSnackbar(
              "Failed to load vendor-team member information",
              { variant: "error" }
            );
          }
        }
      };

      void fetchTeamMembers();
    },
    [activeTab]
  );

  if (activeTab !== ProposalFieldCategory.Team) return null;
  if (Masked.isMasked(proposal.vendorTeam) ||
    Masked.isMasked(proposal.teamRestricted)) {
    return "You do not have permission to view this team."
  }
  return (
    <>
      <HeadControls>
        <FormControlLabel
          checked={proposal.teamRestricted.value}
          control={
            <Switch
              disabled={true}
              color="primary"
            />
          }
          label={
            proposal.teamRestricted.value ? (
              <strong>Team Restricted</strong>
            ) : (
              "Team Not Restricted"
            )
          }
        />
      </HeadControls>
      <ListContainer container direction="row">
        <Grid size="grow">
          <TeamReview
            leader={selectedLeader}
            members={selectedMembers}
            commentThreads={commentThreads}
            onCommentsClicked={onCommentsClicked}
          />
        </Grid>
      </ListContainer>
    </>
  );
}
